import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { ApiError, CancelablePromise, Links, PageMetadata } from '../generated'

export type PagedResult<T> = {
  _embedded?: {
    listResponse?: Array<T>
  }
  _links?: Links
  page?: PageMetadata
}
export const DEFAULT_PAGE_DATA = {
  number: 0,
  size: 0,
  totalElements: 0,
  totalPages: 0,
}
export type ErrorCode =
  | 'MIN_LENGTH'
  | 'MAX_LENGTH'
  | 'DIGIT_CHAR'
  | 'SPECIAL_CHAR'
  | 'UPPER_CASE_CHAR'
  | 'PASSWORD_MUST_BE_DIFFERENT'
  | 'PASSWORD_DOES_NOT_MATCH'

export type SubErrors = {
  field: string
  message: string
  object: null
  rejectedValue: null
  softlockErrors: Array<ErrorCode>
}

export type ApiErrorBody = {
  apierror: {
    status: string
    timestamp: string
    message: string
    debugMessage: string
    subErrors: Array<SubErrors>
  }
}

export const isInvalidOtpError = (error: ApiError): boolean => {
  const subErrors = (error.body as ApiErrorBody | undefined)?.apierror?.subErrors
  return (
    subErrors?.some((subError) =>
      (subError?.softlockErrors as string[] | undefined)?.includes('INVALID_OTP_CODE')
    ) ?? false
  )
}

type QueryKey =
  | 'systemPrograms'
  | 'systemDevice'
  | 'systemCountries'
  | 'systemDevices'
  | 'systemModels'
  | 'systemDeviceActiveNetworkLock'
  | 'systemProgramConfiguration'
  | 'systemLanguages'
  | 'systemDeviceLogs'
  | 'systemProgramOperators'
  | 'systemClientVersions'
  | 'devices'
  | 'operators'
  | 'currentProgram'
  | 'languageCodes'
  | 'languages'
  | 'deviceGroup'
  | 'bulkActions'
  | 'currentBulkAction'
  | 'latestBulkAction'
  | 'bulkErrors'
  | 'device'
  | 'deviceActiveNetworkLock'
  | 'deviceUnlockPeriod'
  | 'bulkActionWarmUp'
  | 'programNotificationConfiguration'
  | 'programDynamicLocalizedText'
  | 'dynamicLocalizedText'
  | 'allowedNumbers'
  | 'systemDeviceModels'
  | 'allowedApps'
  | 'applicationVersions'
  | 'carePin'
  | 'careAdminDevices'
  | 'careAdminDevicesActiveNetworkLock'
  | 'careAdminDevicesLogs'
  | 'deviceGroupDetails'
  | 'integrationApiUser'
  | 'users'
  | 'softlockEnrollmentMessageRead'
  | 'softlockWhitelistRead'
  | 'softlockLockActionsRead'
  | 'softlockConfigurationPageRead'
  | 'softlockMultipleLocalizedKeysRead'
  | 'softlockProgramNotificationConfigurationRead'
  | 'softlockProgramRead'
  | 'currentUser'
  | 'authorizedAndroidApp'
  | 'systemUnlockHashes'
  | 'otaUpdates'
  | 'systemClientVersionsPaged'
  | 'systemClientVersion'
  | 'systemModelsPaged'
  | 'OTAAPKUploadInfo'
  | 'otaUpdateBulk'
  | 'systemModelsOfClientVersionPaged'
  | 'systemModelsOfClientVersionWithoutOTAPaged'
  | 'deviceList'
  | 'nextReg'
  | 'registrationState'
  | 'programSimSlot'
  | 'systemSimSlot'
  | 'readDefaultexts'
  | 'systemDefaultTextList'
  | 'networkPolicyList'
  | 'operatorControllerRead'
  | 'networkLockPolicyRead'
  | 'operatorDefaultTextList'
  | 'themeConfigurationPageRead'
  | 'tacs'
  | 'themeConfigurationLogoUpload'
  | 'themeConfigurationLogoSanitize'
  | 'fixedLockMessages'
  | 'modelConfigurations'

export type Assembler = {
  forceFirstAndLastRels?: boolean
}

export type Sort = { fieldName: string | string[]; direction: 'asc' | 'desc' }
export type BaseParameters = {
  assembler: Assembler
  page?: number
  size?: number
  sort?: Array<string>
}

type ListFn<T, O extends BaseParameters> = (options: O) => CancelablePromise<T>
type ReadFn<T, O> = (options: O) => CancelablePromise<T>

export const defaultAssembler = (): Assembler => ({ forceFirstAndLastRels: false })

/** when using this function if you get ts error telling pageable is missing. There is propably ParameterObject annotation missing in backend end point */
export function useList<T, O extends BaseParameters>(
  fetchFn: ListFn<T, O>,
  queryKey: QueryKey,
  parameters: Omit<O, keyof BaseParameters> & Partial<Pick<O, keyof BaseParameters>>,
  options?: Omit<UseQueryOptions<T, unknown, T, readonly unknown[]>, 'initialData'> & {
    initialData?: () => undefined
  }
) {
  const parametersWithDefaults: typeof parameters = {
    page: 0,
    size: 20,
    ...parameters,
  }
  parametersWithDefaults.assembler = {
    forceFirstAndLastRels: false,
    ...parameters.assembler,
  }

  return useQuery({
    queryKey: [queryKey, parameters],
    queryFn: () => fetchFn(parametersWithDefaults as O),
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    ...options,
  })
}

export function useListAll<T>(fetchFn: ListFn<T, BaseParameters>, queryKey: QueryKey) {
  return useList(fetchFn, queryKey, { page: 0, size: 1000 })
}

export function useRead<T, O>(
  fetchFn: ReadFn<T, O>,
  queryKey: QueryKey,
  parameters?: O,
  options?: UseQueryOptions<T, unknown, T, readonly unknown[]>
) {
  const queryKeys = [queryKey, parameters]
  return useQuery({
    queryKey: queryKeys,
    queryFn: () => fetchFn(parameters as O),
    refetchOnWindowFocus: false,
    ...options,
  })
}

export function useReadWithNotFound<T, O>(
  fetchFn: (params: O) => Promise<T>,
  queryKey: QueryKey,
  parameters?: O,
  options?: UseQueryOptions<T, unknown, T, readonly unknown[]>
) {
  return useQuery({
    queryKey: [queryKey, parameters],
    queryFn: () =>
      new Promise<T>((resolve, reject) => {
        fetchFn(parameters as O)
          .then(resolve)
          .catch((error: ApiError) => {
            if (error.status === 404) {
              resolve({ _embedded: undefined } as unknown as T)
            } else {
              reject(error)
            }
          })
      }),
    refetchOnWindowFocus: false,
    ...options,
  })
}

export function pagedDataIsAvailable(data: PageMetadata | undefined) {
  return data && data?.size && data?.totalElements && data?.totalElements > 0
}

export function getSorting(defaultSort?: string[], sort?: Sort): string[] | undefined {
  let sorting: string | string[] | undefined = defaultSort

  if (Array.isArray(sort?.fieldName)) {
    sorting = sort?.fieldName.map((v: string) => `${v},${sort.direction}`)
  } else if (sort) {
    sorting = [`${sort.fieldName},${sort.direction}`]
  }

  return sorting
}
